import { Pencil, StickyNote, Trash2 } from "lucide-react"
import * as React from "react"
import { useNavigate } from "react-router"

import { Alert } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Dialog, DialogClose, DialogContent, DialogTrigger } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/toast"

import { useDeleteRunMutation, useUpdateRunMutation } from "./api"
import {
  EVAL_RUN_STATUS_LABEL,
  EVAL_RUN_STATUSES,
  type EvalRun,
  type EvalRunStatus,
} from "./eval-runs-data"

// An admin's controls for one run: move it through Draft → Reviewed → Archived, rename it, write
// its notes, or delete it. Runs are a development tool: no state shows one to a client.

const isStatus = (v: string): v is EvalRunStatus =>
  (EVAL_RUN_STATUSES as readonly string[]).includes(v)

/** A run's status as a small badge, for the list and for staff without the control. */
export function RunStatusBadge({ status }: { status: EvalRunStatus }) {
  return (
    <span
      className={
        status === "reviewed"
          ? "rounded-full bg-master-tint px-2 py-0.5 text-caption text-master-strong"
          : status === "archived"
            ? "rounded-full bg-surface-sunken px-2 py-0.5 text-caption text-text-3"
            : "rounded-full border border-border px-2 py-0.5 text-caption text-text-3"
      }
    >
      {EVAL_RUN_STATUS_LABEL[status]}
    </span>
  )
}

export function RunStatusControl({ run }: { run: EvalRun }) {
  const update = useUpdateRunMutation(run.id)
  const { toast } = useToast()
  return (
    <Select
      value={run.status}
      disabled={update.isPending}
      onValueChange={(value) => {
        if (!isStatus(value) || value === run.status) return
        update.mutate(
          { status: value },
          {
            onError: (err) => {
              toast({ tone: "error", title: "The status could not be changed", body: err.message })
            },
          },
        )
      }}
    >
      <SelectTrigger
        selectSize="sm"
        className="w-40"
        aria-label="Status"
        title="Admins only. Runs are never shown to clients."
      >
        <span className="flex gap-1">
          <span className="text-text-3">Status</span>
          <SelectValue />
        </span>
      </SelectTrigger>
      <SelectContent>
        {EVAL_RUN_STATUSES.map((s) => (
          <SelectItem key={s} value={s}>
            {EVAL_RUN_STATUS_LABEL[s]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function RenameRunDialog({ run }: { run: EvalRun }) {
  const update = useUpdateRunMutation(run.id)
  const [open, setOpen] = React.useState(false)
  const [name, setName] = React.useState(run.name)
  const save = () => {
    if (!name.trim()) return
    update.mutate(
      { name: name.trim() },
      {
        onSuccess: () => {
          setOpen(false)
        },
      },
    )
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (o) setName(run.name)
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" leadingIcon={<Pencil />}>
          Rename
        </Button>
      </DialogTrigger>
      <DialogContent
        title="Rename the run"
        size="sm"
        footer={
          <div className="ml-auto flex items-center gap-2">
            <DialogClose asChild>
              <Button size="sm">Cancel</Button>
            </DialogClose>
            <Button
              size="sm"
              variant="primary"
              disabled={!name.trim()}
              loading={update.isPending}
              onClick={save}
            >
              Save
            </Button>
          </div>
        }
      >
        <label htmlFor="eval-run-name" className="mb-1 block text-body-sm text-text-2">
          Name
        </label>
        <Input
          id="eval-run-name"
          value={name}
          maxLength={200}
          autoFocus
          onFocus={(e) => {
            e.currentTarget.select()
          }}
          onChange={(e) => {
            setName(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") save()
          }}
        />
        {update.isError && (
          <p className="mt-2 text-caption text-err">The name could not be saved.</p>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function DeleteRunDialog({ run }: { run: EvalRun }) {
  const remove = useDeleteRunMutation(run.id)
  const navigate = useNavigate()
  const { toast } = useToast()
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm" variant="destructive" leadingIcon={<Trash2 />}>
          Delete
        </Button>
      </DialogTrigger>
      <DialogContent
        title={`Delete “${run.name}”?`}
        description={`The run, its ${String(run.labelCount)} labels, every remark on them and the stored PDFs are removed.`}
        size="sm"
        footer={
          <div className="ml-auto flex items-center gap-2">
            <DialogClose asChild>
              <Button size="sm" disabled={remove.isPending}>
                Keep it
              </Button>
            </DialogClose>
            <Button
              size="sm"
              variant="destructive"
              loading={remove.isPending}
              onClick={() => {
                remove.mutate(undefined, {
                  onSuccess: () => {
                    toast({ tone: "success", title: "Run deleted" })
                    void navigate("/evaluations")
                  },
                })
              }}
            >
              Delete run
            </Button>
          </div>
        }
      >
        <Alert tone="warning">
          This can&apos;t be undone. The run can be published again from the metrics workdir.
        </Alert>
        {remove.isError && (
          <p className="mt-2 text-caption text-err">The run could not be deleted.</p>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** Notes on the run, for the admins who open it. */
export function RunNotes({ run, editable }: { run: EvalRun; editable: boolean }) {
  const update = useUpdateRunMutation(run.id)
  const [editing, setEditing] = React.useState(false)
  const [draft, setDraft] = React.useState(run.notes ?? "")

  if (!editing) {
    if (!run.notes && !editable) return null
    return (
      <section className="rounded-md border border-border bg-surface-sunken p-3" aria-label="Notes">
        <div className="mb-1 flex items-center gap-2 text-caption font-semibold text-text-3">
          <StickyNote className="size-3.5" aria-hidden /> Notes
          {editable && (
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto"
              onClick={() => {
                setDraft(run.notes ?? "")
                setEditing(true)
              }}
            >
              {run.notes ? "Edit notes" : "Add notes"}
            </Button>
          )}
        </div>
        {run.notes ? (
          <p className="whitespace-pre-wrap text-body-sm text-text">{run.notes}</p>
        ) : (
          <p className="text-caption text-text-3">
            No notes yet: what changed in this run, and what to look at.
          </p>
        )}
      </section>
    )
  }
  return (
    <form
      className="space-y-2 rounded-md border border-border bg-surface p-3"
      onSubmit={(e) => {
        e.preventDefault()
        update.mutate(
          { notes: draft },
          {
            onSuccess: () => {
              setEditing(false)
            },
          },
        )
      }}
    >
      <label htmlFor="eval-run-notes" className="text-caption font-semibold text-text-3">
        Notes
      </label>
      <Textarea
        id="eval-run-notes"
        minRows={4}
        maxLength={10000}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value)
        }}
      />
      {update.isError && <p className="text-caption text-err">The notes could not be saved.</p>}
      <div className="flex gap-2">
        <Button type="submit" size="sm" variant="primary" loading={update.isPending}>
          Save notes
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            setEditing(false)
          }}
        >
          Cancel
        </Button>
      </div>
    </form>
  )
}
